/**
 * Calendar days as the `YYYY-MM-DD` strings the rest of the app keeps them in,
 * and as the `Date` a month grid wants.
 *
 * A day here is a name, never an instant: it is built from and read back
 * through the local year, month and day, so no timezone can move it. Going
 * through `toISOString()` would, for anyone east of Greenwich, hand back the
 * day before the one that was clicked.
 */

const ISO_DAY = /^(\d{4})-(\d{2})-(\d{2})$/;
const TYPED_DAY = /^(\d{1,2})[/.-](\d{1,2})[/.-](\d{4})$/;

function build(year: number, month: number, day: number): Date | undefined {
  const date = new Date(year, month - 1, day);
  // `new Date(2026, 1, 31)` is 3 March; a day that does not round-trip was
  // never a day.
  return date.getFullYear() === year &&
    date.getMonth() === month - 1 &&
    date.getDate() === day
    ? date
    : undefined;
}

export function parseDay(value: string | undefined | null): Date | undefined {
  const match = ISO_DAY.exec(value ?? "");
  if (!match) return undefined;
  return build(Number(match[1]), Number(match[2]), Number(match[3]));
}

export function toDay(date: Date): string {
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${String(date.getFullYear()).padStart(4, "0")}-${month}-${day}`;
}

/** What someone types: `2026-07-13`, or day first as it is said — `13/07/2026`. */
export function parseTypedDay(text: string): string | undefined {
  const trimmed = text.trim();
  if (parseDay(trimmed)) return trimmed;
  const match = TYPED_DAY.exec(trimmed);
  if (!match) return undefined;
  const date = build(Number(match[3]), Number(match[2]), Number(match[1]));
  return date ? toDay(date) : undefined;
}

const SHOWN = new Intl.DateTimeFormat("en-IN", {
  day: "2-digit",
  month: "short",
  year: "numeric",
});

/** `13 Jul 2026`, the table's own wording of a day. */
export function showDay(value: string | undefined | null): string {
  const date = parseDay(value);
  return date ? SHOWN.format(date) : "";
}
